import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Pagination,
  IconButton,
  Tooltip,
  Alert,
  Chip,
  Stack,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tab,
  Tabs,
  Avatar,
  CardHeader,
  Divider,
  InputAdornment,
  LinearProgress,
  useTheme
} from "@mui/material";
import { 
  Add, 
  Edit, 
  Delete, 
  Search, 
  Category,
  Warning,
  Inventory2,
  LocalLaundryService,
  TrendingUp,
  TrendingDown,
  Sort,
  FilterList
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { 
  getLinenItems, 
  createLinenItem, 
  updateLinenItem, 
  deleteLinenItem,
  getCategories,
  getLowStockItems
} from "../../services/api";

// Tab panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`linen-tabpanel-${index}`}
      aria-labelledby={`linen-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export const LinenItemMaster = () => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const [items, setItems] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState("");
  
  // Available units from backend schema
  const availableUnits = ["piece", "set", "pair", "kg", "meter"];
  
  const initialFormState = {
    code: "",
    name: "",
    category: "",
    quantity: 0,
    dirty_quantity: 0,
    damaged_quantity: 0,
    min_stock: 0,
    unit: "piece",
    description: "",
    status: "active"
  };
  
  const [form, setForm] = useState(initialFormState);

  // Load categories
  const loadCategories = useCallback(async () => {
    try {
      setLoadingCategories(true);
      const res = await getCategories();
      setCategories(res.data?.categories || []);
    } catch (err) {
      console.error("Load categories error:", err);
      enqueueSnackbar("Failed to load categories", { variant: "error" });
    } finally {
      setLoadingCategories(false);
    }
  }, [enqueueSnackbar]);

  // Load low stock items
  const loadLowStockItems = useCallback(async () => {
    try {
      const res = await getLowStockItems();
      setLowStockItems(res.data?.items || []);
    } catch (err) {
      console.error("Load low stock items error:", err);
      enqueueSnackbar("Failed to load low stock items", { variant: "error" });
    }
  }, [enqueueSnackbar]);

  // Memoized load function
  const loadItems = useCallback(async (pageNum = 1, categoryFilter = selectedCategory) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = { 
        page: pageNum, 
        limit, 
        search: search.trim(),
        status: "active"
      };
      
      // Add category filter if selected
      if (categoryFilter) {
        params.category = categoryFilter;
      }
      
      const res = await getLinenItems(params);
      
      const data = res.data || {};
      const itemsList = data.items || [];
      const totalItems = data.pagination?.total || data.total || itemsList.length;
      
      setItems(itemsList);
      setTotalCount(totalItems);
      
      const calculatedPages = data.pagination?.pages || 
                            Math.max(1, Math.ceil(totalItems / limit));
      setTotalPages(calculatedPages);
      setPage(pageNum);
      
    } catch (err) {
      console.error("Load items error:", err);
      const errorMessage = err.response?.data?.message || "Failed to load items";
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: "error" });
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [search, limit, selectedCategory, enqueueSnackbar]);

  // Initial load
  useEffect(() => {
    loadItems(1);
    loadCategories();
    loadLowStockItems();
  }, [loadItems, loadCategories, loadLowStockItems]);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadItems(1);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [search, loadItems]);

  // Handle category filter change
  useEffect(() => {
    loadItems(1);
  }, [selectedCategory, loadItems]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleAdd = () => {
    setEditingId(null);
    setForm(initialFormState);
    setOpen(true);
  };

  const handleEdit = (item) => {
    setEditingId(item._id);
    setForm({
      code: item.code || "",
      name: item.name || "",
      category: item.category || "",
      quantity: item.quantity || 0,
      dirty_quantity: item.dirty_quantity || 0,
      damaged_quantity: item.damaged_quantity || 0,
      min_stock: item.min_stock || 0,
      unit: item.unit || "piece",
      description: item.description || "",
      status: item.status || "active"
    });
    setOpen(true);
  };

  const handleSave = async () => {
    // Enhanced validation
    if (!form.name.trim()) {
      enqueueSnackbar("Name is required", { variant: "warning" });
      return;
    }

    if (!form.code.trim()) {
      enqueueSnackbar("Item code is required", { variant: "warning" });
      return;
    }

    if (form.quantity < 0 || form.dirty_quantity < 0 || form.damaged_quantity < 0 || form.min_stock < 0) {
      enqueueSnackbar("Quantities cannot be negative", { variant: "warning" });
      return;
    }

    // Validate available quantity
    const availableQuantity = form.quantity - (form.dirty_quantity + form.damaged_quantity);
    if (availableQuantity < 0) {
      enqueueSnackbar("Available quantity cannot be negative", { variant: "warning" });
      return;
    }

    try {
      setSaving(true);
      
      const payload = {
        ...form,
        code: form.code.toUpperCase().trim(), // Normalize code
        name: form.name.trim(),
        category: form.category.trim(),
        description: form.description.trim()
      };
      
      if (editingId) {
        await updateLinenItem(editingId, payload);
        enqueueSnackbar("🎉 Item updated successfully", { variant: "success" });
      } else {
        await createLinenItem(payload);
        enqueueSnackbar("🎉 Item created successfully", { variant: "success" });
      }
      
      setOpen(false);
      loadItems(page);
      loadLowStockItems(); // Refresh low stock items
    } catch (err) {
      console.error("Save error:", err);
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.errors?.join(", ") || 
                          "Save failed";
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
    
    try {
      await deleteLinenItem(id);
      enqueueSnackbar("🗑️ Item deleted successfully", { variant: "success" });
      loadItems(page);
      loadLowStockItems(); // Refresh low stock items
    } catch (err) {
      console.error("Delete error:", err);
      const errorMessage = err.response?.data?.message || "Delete failed";
      enqueueSnackbar(errorMessage, { variant: "error" });
    }
  };

  const handlePageChange = (event, value) => {
    loadItems(value);
  };

  const handleCategoryFilterChange = (event) => {
    setSelectedCategory(event.target.value);
  };

  const getStockStatus = (item) => {
    const availableQuantity = item.quantity - (item.dirty_quantity + item.damaged_quantity);
    if (availableQuantity <= 0) return { status: "out-of-stock", label: "Out of Stock", color: "error" };
    if (availableQuantity < item.min_stock) return { status: "low-stock", label: "Low Stock", color: "warning" };
    return { status: "in-stock", label: "In Stock", color: "success" };
  };

  const calculateAvailableQuantity = (item) => {
    return Math.max(0, item.quantity - (item.dirty_quantity + item.damaged_quantity));
  };

  // Calculate statistics
  const calculateStats = () => {
    const totalItems = items.length;
    const lowStockCount = lowStockItems.length;
    const outOfStockCount = items.filter(item => calculateAvailableQuantity(item) === 0).length;
    const totalAvailable = items.reduce((sum, item) => sum + calculateAvailableQuantity(item), 0);
    
    return {
      totalItems,
      lowStockCount,
      outOfStockCount,
      totalAvailable
    };
  };

  const stats = calculateStats();

  // Render table rows for items
  const renderItemRow = (item) => {
    const stockStatus = getStockStatus(item);
    const availableQuantity = calculateAvailableQuantity(item);
    
    return (
      <TableRow 
        key={item._id}
        hover
        sx={{ 
          '&:last-child td, &:last-child th': { border: 0 },
          '&:hover': {
            backgroundColor: 'primary.light',
            transform: 'translateY(-1px)',
            transition: 'all 0.3s ease'
          },
          transition: 'all 0.2s ease'
        }}
      >
        <TableCell>
          <Typography variant="body1" fontWeight={700} sx={{ fontSize: '1.1rem' }}>
            {item.code || "N/A"}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body1" fontWeight={600} sx={{ fontSize: '1.1rem' }}>
            {item.name}
          </Typography>
          {item.description && (
            <Typography variant="body2" color="text.secondary" display="block" sx={{ fontSize: '1rem' }}>
              {item.description}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Chip 
            label={item.category || "Uncategorized"} 
            size="medium"
            variant="outlined"
            sx={{ fontSize: '1rem', fontWeight: 600 }}
          />
        </TableCell>
        <TableCell align="center">
          <Typography variant="body1" fontWeight={700} sx={{ fontSize: '1.1rem' }}>
            {item.quantity}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Typography variant="body1" color="warning.main" fontWeight={600} sx={{ fontSize: '1.1rem' }}>
            {item.dirty_quantity}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Typography variant="body1" color="error.main" fontWeight={600} sx={{ fontSize: '1.1rem' }}>
            {item.damaged_quantity}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Typography variant="body1" fontWeight={700} sx={{ fontSize: '1.1rem' }}>
            {availableQuantity}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
            {item.min_stock}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Chip 
            label={stockStatus.label}
            color={stockStatus.color}
            size="medium"
            sx={{ fontSize: '1rem', fontWeight: 600, minWidth: 100 }}
          />
        </TableCell>
        <TableCell align="right">
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Tooltip title="Edit Item">
              <IconButton 
                size="large" 
                onClick={() => handleEdit(item)}
                sx={{ 
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': { backgroundColor: 'primary.dark' }
                }}
              >
                <Edit sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Item">
              <IconButton 
                size="large" 
                onClick={() => handleDelete(item._id, item.name)}
                sx={{ 
                  backgroundColor: 'error.main',
                  color: 'white',
                  '&:hover': { backgroundColor: 'error.dark' }
                }}
              >
                <Delete sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Box sx={{ p: 4, backgroundColor: 'grey.50', minHeight: '100vh' }}>
      {/* Header Section */}
      <Card sx={{ 
        mb: 4, 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        borderRadius: 3
      }}>
        <CardContent sx={{ p: 4 }}>
          <Grid container alignItems="center" spacing={3}>
            <Grid item>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <LocalLaundryService sx={{ fontSize: 48, color: 'white' }} />
              </Avatar>
            </Grid>
            <Grid item xs>
              <Typography 
                variant="h2" 
                fontWeight={800}
                sx={{ 
                  color: 'white',
                  fontSize: { xs: '2.5rem', md: '3rem' },
                  mb: 1
                }}
              >
                Linen Item Master
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'white', 
                  opacity: 0.9,
                  fontSize: '1.3rem'
                }}
              >
                Manage and track all linen inventory items
              </Typography>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                startIcon={<Add sx={{ fontSize: 28 }} />}
                onClick={handleAdd}
                sx={{
                  backgroundColor: 'white',
                  color: 'primary.main',
                  fontSize: '1.2rem',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  '&:hover': {
                    backgroundColor: 'grey.100',
                    transform: 'translateY(-2px)',
                    boxShadow: 3
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Add Item
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Avatar sx={{ backgroundColor: 'primary.light', mb: 2, mx: 'auto' }}>
                <Inventory2 sx={{ fontSize: 32, color: 'primary.main' }} />
              </Avatar>
              <Typography variant="h2" sx={{ fontSize: '2.5rem', fontWeight: 800, color: 'primary.main', mb: 1 }}>
                {stats.totalItems}
              </Typography>
              <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
                Total Items
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Avatar sx={{ backgroundColor: 'success.light', mb: 2, mx: 'auto' }}>
                <TrendingUp sx={{ fontSize: 32, color: 'success.main' }} />
              </Avatar>
              <Typography variant="h2" sx={{ fontSize: '2.5rem', fontWeight: 800, color: 'success.main', mb: 1 }}>
                {stats.totalAvailable}
              </Typography>
              <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
                Available Stock
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Avatar sx={{ backgroundColor: 'warning.light', mb: 2, mx: 'auto' }}>
                <Warning sx={{ fontSize: 32, color: 'warning.main' }} />
              </Avatar>
              <Typography variant="h2" sx={{ fontSize: '2.5rem', fontWeight: 800, color: 'warning.main', mb: 1 }}>
                {stats.lowStockCount}
              </Typography>
              <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
                Low Stock
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Avatar sx={{ backgroundColor: 'error.light', mb: 2, mx: 'auto' }}>
                <TrendingDown sx={{ fontSize: 32, color: 'error.main' }} />
              </Avatar>
              <Typography variant="h2" sx={{ fontSize: '2.5rem', fontWeight: 800, color: 'error.main', mb: 1 }}>
                {stats.outOfStockCount}
              </Typography>
              <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
                Out of Stock
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
        <CardHeader
          title={
            <Typography variant="h4" fontWeight={700} sx={{ fontSize: '1.6rem' }}>
              📦 Linen Inventory Management
            </Typography>
          }
          subheader={
            <Typography sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
              Manage your linen items, track stock levels, and monitor inventory health
            </Typography>
          }
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'grey.50'
          }}
        />
        
        <CardContent>
          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab 
                icon={<Inventory2 sx={{ fontSize: 24 }} />} 
                iconPosition="start"
                label={
                  <Typography sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                    All Items ({totalCount})
                  </Typography>
                }
              />
              <Tab 
                icon={<Warning sx={{ fontSize: 24 }} />} 
                iconPosition="start"
                label={
                  <Typography sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                    Low Stock ({lowStockItems.length})
                  </Typography>
                }
              />
            </Tabs>
          </Box>

          {/* Search and Filters Section */}
          <Grid container spacing={3} alignItems="center" sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search by name, code, or category..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "primary.main", fontSize: 28 }} />
                    </InputAdornment>
                  ),
                  sx: {
                    fontSize: '1.1rem',
                    '& input': {
                      padding: '12px 14px',
                      fontSize: '1.1rem'
                    }
                  }
                }}
                size="medium"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="medium">
                <InputLabel sx={{ fontSize: '1.1rem' }}>Category Filter</InputLabel>
                <Select
                  value={selectedCategory}
                  onChange={handleCategoryFilterChange}
                  label="Category Filter"
                  sx={{ fontSize: '1.1rem' }}
                  startAdornment={
                    <InputAdornment position="start">
                      <FilterList sx={{ color: 'primary.main', fontSize: 28 }} />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="" sx={{ fontSize: '1.1rem' }}>All Categories</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category} value={category} sx={{ fontSize: '1.1rem' }}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={5} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Button 
                startIcon={<Add sx={{ fontSize: 28 }} />} 
                variant="contained" 
                onClick={handleAdd}
                sx={{ 
                  fontSize: '1.2rem',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  minWidth: 160
                }}
              >
                Add New Item
              </Button>
            </Grid>
          </Grid>

          {/* Error Alert */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                fontSize: '1.1rem',
                borderRadius: 3,
                '& .MuiAlert-message': { fontSize: '1.1rem' }
              }}
            >
              {error}
            </Alert>
          )}

          {/* Tab Panels */}
          <TabPanel value={tabValue} index={0}>
            {/* All Items */}
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8, flexDirection: 'column', gap: 2 }}>
                <CircularProgress size={60} thickness={4} />
                <Typography variant="h4" sx={{ fontSize: '1.8rem', fontWeight: 600 }}>
                  Loading Items...
                </Typography>
                <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
                  Preparing your linen inventory
                </Typography>
              </Box>
            ) : (
              <>
                <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary', mb: 3 }}>
                  📊 Showing {items.length} of {totalCount} items
                </Typography>

                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
                  <Table>
                    <TableHead sx={{ backgroundColor: 'primary.main' }}>
                      <TableRow>
                        <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          🏷️ Item Code
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          📋 Item Name
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          🗂️ Category
                        </TableCell>
                        <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          🧼 Clean
                        </TableCell>
                        <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          🟡 Dirty
                        </TableCell>
                        <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          🔴 Damaged
                        </TableCell>
                        <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          📦 Available
                        </TableCell>
                        <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          ⚠️ Min Stock
                        </TableCell>
                        <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          📊 Status
                        </TableCell>
                        <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          🛠️ Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.length > 0 ? (
                        items.map(renderItemRow)
                      ) : (
                        <TableRow>
                          <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                            <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                              <Inventory2 sx={{ fontSize: 80, mb: 2, opacity: 0.3 }} />
                              <Typography variant="h4" sx={{ fontSize: '1.8rem', mb: 1, fontWeight: 600 }}>
                                No Items Found
                              </Typography>
                              <Typography variant="h6" sx={{ fontSize: '1.2rem', mb: 3 }}>
                                {search || selectedCategory ? "No items found matching your criteria" : "Get started by adding your first linen item"}
                              </Typography>
                              <Button 
                                variant="contained" 
                                startIcon={<Add sx={{ fontSize: 28 }} />}
                                onClick={handleAdd}
                                sx={{ 
                                  fontSize: '1.2rem',
                                  px: 4,
                                  py: 1.5,
                                  borderRadius: 3
                                }}
                              >
                                Add First Item
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Pagination 
                      count={totalPages} 
                      page={page} 
                      onChange={handlePageChange}
                      color="primary"
                      size="large"
                      showFirstButton
                      showLastButton
                      sx={{
                        '& .MuiPaginationItem-root': {
                          fontSize: '1.1rem',
                          fontWeight: 600
                        }
                      }}
                    />
                  </Box>
                )}
              </>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {/* Low Stock Items */}
            <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary', mb: 3 }}>
              ⚠️ Items requiring immediate attention
            </Typography>

            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
              <Table>
                <TableHead sx={{ backgroundColor: 'warning.main' }}>
                  <TableRow>
                    <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                      🏷️ Item Code
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                      📋 Item Name
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                      🗂️ Category
                    </TableCell>
                    <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                      📦 Available
                    </TableCell>
                    <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                      ⚠️ Min Stock
                    </TableCell>
                    <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                      📉 Deficit
                    </TableCell>
                    <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                      🛠️ Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lowStockItems.length > 0 ? (
                    lowStockItems.map((item) => {
                      const availableQuantity = calculateAvailableQuantity(item);
                      const deficit = item.min_stock - availableQuantity;
                      
                      return (
                        <TableRow key={item._id} hover sx={{ 
                          '&:hover': {
                            backgroundColor: 'warning.light',
                            transform: 'translateY(-1px)',
                            transition: 'all 0.3s ease'
                          },
                          transition: 'all 0.2s ease'
                        }}>
                          <TableCell>
                            <Typography variant="body1" fontWeight={700} sx={{ fontSize: '1.1rem' }}>
                              {item.code}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body1" fontWeight={600} sx={{ fontSize: '1.1rem' }}>
                              {item.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={item.category} 
                              size="medium" 
                              variant="outlined" 
                              sx={{ fontSize: '1rem', fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Typography 
                              variant="body1" 
                              color={availableQuantity === 0 ? "error" : "warning.main"}
                              fontWeight={700}
                              sx={{ fontSize: '1.1rem' }}
                            >
                              {availableQuantity}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                              {item.min_stock}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip 
                              label={`-${deficit}`} 
                              color="error" 
                              size="medium"
                              sx={{ fontSize: '1rem', fontWeight: 600, minWidth: 70 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Edit Item">
                              <IconButton 
                                size="large" 
                                onClick={() => handleEdit(item)}
                                sx={{ 
                                  backgroundColor: 'primary.main',
                                  color: 'white',
                                  '&:hover': { backgroundColor: 'primary.dark' }
                                }}
                              >
                                <Edit sx={{ fontSize: 20 }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                        <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                          <TrendingUp sx={{ fontSize: 80, mb: 2, opacity: 0.3 }} />
                          <Typography variant="h4" sx={{ fontSize: '1.8rem', mb: 1, fontWeight: 600 }}>
                            All Items Well Stocked
                          </Typography>
                          <Typography variant="h6" sx={{ fontSize: '1.2rem' }}>
                            Great job! No low stock items at the moment.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog 
        open={open} 
        onClose={() => !saving && setOpen(false)} 
        maxWidth="sm" 
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            boxShadow: '0 16px 48px rgba(0,0,0,0.2)'
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: 'primary.main', 
          color: 'white',
          py: 3
        }}>
          <Typography variant="h3" sx={{ fontSize: '1.8rem', fontWeight: 700 }}>
            {editingId ? "✏️ Edit Linen Item" : "➕ Add New Linen Item"}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Item Code *"
              value={form.code}
              onChange={(e) => setForm({...form, code: e.target.value})}
              fullWidth
              size="medium"
              required
              error={!form.code.trim()}
              helperText={!form.code.trim() ? "Item code is required" : "Unique identifier for the item"}
              InputProps={{
                sx: { fontSize: '1.1rem' }
              }}
              InputLabelProps={{
                sx: { fontSize: '1.1rem' }
              }}
              FormHelperTextProps={{
                sx: { fontSize: '1rem' }
              }}
            />
            <TextField
              label="Item Name *"
              value={form.name}
              onChange={(e) => setForm({...form, name: e.target.value})}
              fullWidth
              required
              size="medium"
              error={!form.name.trim()}
              helperText={!form.name.trim() ? "Name is required" : ""}
              InputProps={{
                sx: { fontSize: '1.1rem' }
              }}
              InputLabelProps={{
                sx: { fontSize: '1.1rem' }
              }}
            />
            <TextField
              label="Category"
              value={form.category}
              onChange={(e) => setForm({...form, category: e.target.value})}
              fullWidth
              size="medium"
              helperText="e.g., Bed Linens, Bath Linens, Table Linens"
              InputProps={{
                sx: { fontSize: '1.1rem' }
              }}
              InputLabelProps={{
                sx: { fontSize: '1.1rem' }
              }}
            />
            <Grid container spacing={2}>
              <Grid item xs={8}>
                <TextField
                  label="Clean Quantity"
                  type="number"
                  value={form.quantity}
                  onChange={(e) => setForm({...form, quantity: e.target.value})}
                  fullWidth
                  size="medium"
                  inputProps={{ 
                    min: 0,
                    style: { fontSize: '1.1rem' }
                  }}
                  InputLabelProps={{
                    sx: { fontSize: '1.1rem' }
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth size="medium">
                  <InputLabel sx={{ fontSize: '1.1rem' }}>Unit</InputLabel>
                  <Select
                    value={form.unit}
                    onChange={(e) => setForm({...form, unit: e.target.value})}
                    label="Unit"
                    sx={{ fontSize: '1.1rem' }}
                  >
                    {availableUnits.map((unit) => (
                      <MenuItem key={unit} value={unit} sx={{ fontSize: '1.1rem' }}>
                        {unit}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <TextField
              label="Dirty Quantity"
              type="number"
              value={form.dirty_quantity}
              onChange={(e) => setForm({...form, dirty_quantity: e.target.value})}
              fullWidth
              size="medium"
              inputProps={{ 
                min: 0,
                style: { fontSize: '1.1rem' }
              }}
              InputLabelProps={{
                sx: { fontSize: '1.1rem' }
              }}
            />
            <TextField
              label="Damaged Quantity"
              type="number"
              value={form.damaged_quantity}
              onChange={(e) => setForm({...form, damaged_quantity: e.target.value})}
              fullWidth
              size="medium"
              inputProps={{ 
                min: 0,
                style: { fontSize: '1.1rem' }
              }}
              InputLabelProps={{
                sx: { fontSize: '1.1rem' }
              }}
            />
            <TextField
              label="Minimum Stock Level"
              type="number"
              value={form.min_stock}
              onChange={(e) => setForm({...form, min_stock: e.target.value})}
              fullWidth
              size="medium"
              inputProps={{ 
                min: 0,
                style: { fontSize: '1.1rem' }
              }}
              InputLabelProps={{
                sx: { fontSize: '1.1rem' }
              }}
              helperText="Alert when stock falls below this level"
              FormHelperTextProps={{
                sx: { fontSize: '1rem' }
              }}
            />
            <TextField
              label="Description"
              multiline
              rows={3}
              value={form.description}
              onChange={(e) => setForm({...form, description: e.target.value})}
              fullWidth
              size="medium"
              InputProps={{
                sx: { fontSize: '1.1rem' }
              }}
              InputLabelProps={{
                sx: { fontSize: '1.1rem' }
              }}
            />
            {editingId && (
              <FormControl fullWidth size="medium">
                <InputLabel sx={{ fontSize: '1.1rem' }}>Status</InputLabel>
                <Select
                  value={form.status}
                  onChange={(e) => setForm({...form, status: e.target.value})}
                  label="Status"
                  sx={{ fontSize: '1.1rem' }}
                >
                  <MenuItem value="active" sx={{ fontSize: '1.1rem' }}>Active</MenuItem>
                  <MenuItem value="inactive" sx={{ fontSize: '1.1rem' }}>Inactive</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={() => setOpen(false)} 
            disabled={saving}
            size="large"
            sx={{ 
              fontSize: '1.1rem',
              px: 4,
              py: 1
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSave} 
            disabled={saving || !form.name.trim() || !form.code.trim()}
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
            size="large"
            sx={{ 
              fontSize: '1.1rem',
              fontWeight: 600,
              px: 4,
              py: 1,
              minWidth: 140
            }}
          >
            {saving ? 'Saving...' : (editingId ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};